import { Octokit } from "@octokit/core";
import {
  doc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
} from "@firebase/firestore";
import db from "services/firebase";

interface DependabotAlert {
  number: number;
  state: "open" | "dismissed" | "fixed" | "auto_dismissed";
  dependency: {
    package?: {
      ecosystem: string;
      name: string;
    };
    manifest_path?: string;
    scope?: "development" | "runtime" | "unknown" | null;
  };
  security_advisory: {
    ghsa_id: string;
    cve_id: string | null;
    summary: string;
    description: string;
    severity: "low" | "medium" | "high" | "critical";
    published_at: string;
    updated_at: string;
    identifiers?: readonly { type: "CVE" | "GHSA"; value: string }[];
    references?: readonly { url: string }[];
  };
  created_at: string;
  updated_at: string;
  html_url: string;
  dismissed_at?: string | null;
  dismissed_by?: { login: string } | null;
  dismissed_reason?: string | null;
  dismissed_comment?: string | null;
  auto_dismissed_at?: string | null;
}

interface DependabotResponse {
  success: boolean;
  alerts?: DependabotAlert[];
  error?: string;
  disabledRepos?: string[];
}

interface VulnerabilityData {
  count: number;
  vulnerabilities: Array<{
    ghsa_id: string;
    cve_id: string | null;
    severity: string;
    package: string;
    ecosystem: string;
    summary: string;
    url: string;
  }>;
  last_checked: string;
  status: "vulnerable" | "dependabot_disabled" | "no_issues"; 
}

export const createDependabotService = (accessToken: string) => {
  const octokit = new Octokit({
    auth: accessToken,
  });

  const checkDependabotAlerts = async (
    owner: string,
    repo: string
  ): Promise<DependabotResponse> => {
    try {
      const { data } = await octokit.request(
        "GET /repos/{owner}/{repo}/dependabot/alerts",
        {
          owner,
          repo,
          state: "open",
          per_page: 100,
        }
      );

      return {
        success: true,
        alerts: data,
      };
    } catch (error: any) {
      if (
        error.status === 403 &&
        error.message.includes("Dependabot alerts are disabled")
      ) {
        return {
          success: false,
          error: "Dependabot alerts are disabled",
          disabledRepos: [`${owner}/${repo}`],
        };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  const saveDependabotVulnerabilities = async (
    tenantId: string,
    repoUrl: string,
    alerts: DependabotAlert[] | null,
    status: "vulnerable" | "dependabot_disabled" | "no_issues",
    integrationId: string
  ) => {
    try {
      const vulnerabilityData: VulnerabilityData = {
        count: alerts ? alerts.length : 0,
        vulnerabilities: alerts
          ? alerts.map((alert) => ({
              ghsa_id: alert.security_advisory.ghsa_id,
              cve_id: alert.security_advisory.cve_id,
              severity: alert.security_advisory.severity,
              package: alert.dependency.package?.name ?? "unknown",
              ecosystem: alert.dependency.package?.ecosystem ?? "unknown",
              summary: alert.security_advisory.summary,
              url: alert.html_url,
            }))
          : [],
        last_checked: new Date().toISOString(),
        status,
      };

      const tenantRef = doc(db, "tenants", tenantId);
      const vulnerabilitiesRef = collection(tenantRef, "vulnerabilities");
      const batch = writeBatch(db);

      if (status === "vulnerable" && alerts) {
        for (const vulnerability of vulnerabilityData.vulnerabilities) {
          const existingVulnQuery = query(
            vulnerabilitiesRef,
            where("ghsa_id", "==", vulnerability.ghsa_id),
            where("repo_url", "==", repoUrl)
          );
          const existingVulnSnapshot = await getDocs(existingVulnQuery);

          if (existingVulnSnapshot.empty) {
            batch.set(doc(vulnerabilitiesRef), {
              ...vulnerability,
              repo_url: repoUrl,
              source: "Github Dependabot",
              created_at: new Date().toISOString(),
              status: "open",
              remediation_status: "pending",
              scan_date: vulnerabilityData.last_checked,
            });
          } else {
            const existingDoc = existingVulnSnapshot.docs[0];
            batch.update(doc(vulnerabilitiesRef, existingDoc.id), {
              scan_date: vulnerabilityData.last_checked,
            });
          }
        }
      } else if (status === "dependabot_disabled") {
        const disabledVulnQuery = query(
          vulnerabilitiesRef,
          where("repo_url", "==", repoUrl),
          where("status", "==", "dependabot_disabled")
        );
        const disabledVulnSnapshot = await getDocs(disabledVulnQuery);

        if (disabledVulnSnapshot.empty) {
          batch.set(doc(vulnerabilitiesRef), {
            ghsa_id: `disabled-${repoUrl.replace(/[^a-zA-Z0-9]/g, "-")}`,
            cve_id: null,
            severity: "medium",
            ecosystem: "Disable",
            package: "dependabot_disabled",
            summary: "Dependabot alerts are disabled for this repository",
            url: repoUrl,
            repo_url: repoUrl,
            source: "Github Dependabot",
            created_at: new Date().toISOString(),
            status: "open",
            type: "github_dependabot",
            remediation_status: "pending",
            scan_date: vulnerabilityData.last_checked,
          });
        } else {
          const existingDoc = disabledVulnSnapshot.docs[0];
          batch.update(doc(vulnerabilitiesRef, existingDoc.id), {
            scan_date: vulnerabilityData.last_checked,
          });
        }
      } else if (status === "no_issues") {
        const disabledVulnQuery = query(
          vulnerabilitiesRef,
          where("repo_url", "==", repoUrl),
          where("status", "==", "dependabot_disabled")
        );
        const disabledVulnSnapshot = await getDocs(disabledVulnQuery);
        disabledVulnSnapshot.forEach((docSnap) => {
          batch.delete(doc(vulnerabilitiesRef, docSnap.id));
        });
      }

      const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
      batch.update(integrationRef, {
        disabled_repos: [], 
        last_scan: new Date().toISOString(),
      });

      await batch.commit();
      return { success: true };
    } catch (error: any) {
      console.error("Error saving Dependabot vulnerabilities:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };

  const scanRepositoriesForVulnerabilities = async (
    repositories: Array<{
      owner: { login: string };
      name: string;
      html_url: string;
    }>,
    tenantId: string,
    integrationId: string
  ) => {
    try {
      let vulnerabilityCount = 0;
      const disabledRepos: string[] = [];
      const batch = writeBatch(db);

      for (const repo of repositories) {
        const result = await checkDependabotAlerts(repo.owner.login, repo.name);

        if (result.success && result.alerts) {
          const status = result.alerts.length > 0 ? "vulnerable" : "no_issues";
          vulnerabilityCount += result.alerts.length;
          const saveResult = await saveDependabotVulnerabilities(
            tenantId,
            repo.html_url,
            result.alerts,
            status,
            integrationId
          );
          if (!saveResult.success) {
            console.error(`Failed to save alerts for ${repo.html_url}:`, saveResult.error);
          }
        } else if (result.disabledRepos) {
          disabledRepos.push(...result.disabledRepos);
          const saveResult = await saveDependabotVulnerabilities(
            tenantId,
            repo.html_url,
            null,
            "dependabot_disabled",
            integrationId
          );
          if (!saveResult.success) {
            console.error(`Failed to save disabled status for ${repo.html_url}:`, saveResult.error);
          }
        }
      }

      if (disabledRepos.length > 0) {
        const integrationRef = doc(db, "tenants", tenantId, "integrations", integrationId);
        batch.update(integrationRef, {
          disabled_repos: disabledRepos,
          last_scan: new Date().toISOString(),
        });
        await batch.commit();
      }

      if (disabledRepos.length > 0) {
        return {
          success: false,
          error: "Dependabot alerts disabled for some repositories",
          disabledRepos,
          totalVulnerabilities: vulnerabilityCount,
          scannedRepos: repositories.length,
        };
      }

      return {
        success: true,
        totalVulnerabilities: vulnerabilityCount,
        scannedRepos: repositories.length,
      };
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      return {
        success: false,
        error: errorMessage,
        disabledRepos: [],
        totalVulnerabilities: 0,
        scannedRepos: repositories.length,
      };
    }
  };

  return {
    checkDependabotAlerts,
    saveDependabotVulnerabilities,
    scanRepositoriesForVulnerabilities,
  };
};

export default createDependabotService;